import classnames from 'classnames';
import { AlertTriangle, Info } from 'lucide-react';

interface Props {
  variant: 'warning' | 'info';
  title: string;
  text?: string;
  className?: string;
  testId?: string;
}

/** A short note under the order bar about what can't be ordered right now, and what to do instead. */
export default function AvailabilityNote(props: Props) {
  const { variant, title, text, className, testId } = props;
  const Icon = variant === 'warning' ? AlertTriangle : Info;

  // A note can appear mid-visit (the table dropped, the visit over), so it is announced as well as shown.
  // Polite, and not role="status": the e2e specs find toasts by that role.
  return (
    <div
      aria-live="polite"
      className={classnames(container, variants[variant], className)}
      data-test={testId ?? 'availability-note'}
    >
      <Icon aria-hidden="true" className={classnames('w-[16px] h-[16px] shrink-0 mt-px', iconStyles[variant])} />
      <p className="m-0 min-w-0 flex-1">
        <b className="font-semibold">{title}</b>
        {text && ` ${text}`}
      </p>
    </div>
  );
}

// Values from the design system's Alert (warning, info). Its colours are fixed, not the brand's, so
// they are set here with their dark-mode counterparts; the shop's own `warning` colour is a pink.
const container =
  'flex items-start gap-[12px] rounded-[12px] px-[16px] py-[12px] text-[13px] leading-[1.35] text-brand-text-primary';
const variants = {
  warning: 'bg-[#fffbe6] dark:bg-[#ffc53d]/[0.14]',
  info: 'bg-[#eef4ff] dark:bg-[#8ab4ff]/[0.14]',
};
const iconStyles = {
  warning: 'text-[#9c5e00] dark:text-[#ffc53d]',
  info: 'text-[#1d5fbf] dark:text-[#8ab4ff]',
};
